import { useState, useEffect } from "react";
import { fetchProfile } from "../../Services/profile/profileservice";
import { createMandate } from "../../Services/Mandate/MandateService";
import ViewMandateModal from "./ViewMandate";
import { showSuccessToast, showErrorToast } from "../../utils/toastUtils";
import { Download, Info } from "lucide-react";
const MODES = ["UPI_AUTO_PAY", "NACH"];
const modeLabels = MODES.map((mode) => ({
  value: mode,
  label:
    mode === "UPI_AUTO_PAY" ? "UPI AUTO PAY" : mode === "NACH" ? "NACH" : mode,
}));

const FREQUENCIES = ["ONCE", "DAILY", "WEEKLY",  "MONTHLY", "YEARLY"];

// "BI-WEEKLY",

export default function CreateMandate({ onSuccess, setGlobalLoading }) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState(null);
  const [profile, setProfile] = useState(null);
  const [mandates, setMandates] = useState([]);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedMandate, setSelectedMandate] = useState(null);

  const initialForm = {
    mode: "UPI_AUTO_PAY",
    frequency: "MONTHLY",
    name: "",
    mobile: "",
    amount: "",
    advanceAmount: "0",
    installment_count: "",
    start_date: "",
    description: "",
  };
  const [form, setForm] = useState(initialForm);

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        const response = await fetchProfile();
        setProfile(response.data);
      } catch (error) {
        console.error("Error fetching profile:", error);
      }
    };

    fetchProfileData();
  }, []);

  /* ---------- VALIDATIONS ---------- */

  const totalAmount =
    form.amount && form.installment_count
      ? Number(form.amount) * Number(form.installment_count)
      : "";

  const isInstallmentValid =
    form.installment_count !== "" && Number(form.installment_count) >= 1;

  const isInstallmentAmountValid =
    form.amount !== "" && Number(form.amount) > 15;

  const isMobileValid = /^\d{10}$/.test(form.mobile);

  const isProfileReady =
    !!profile?.user_id && !!profile?.api_key;

  const getMinStartDate = () => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);

    if (form.mode === "NACH") {
      date.setDate(date.getDate() + 4);
    } else {
      date.setDate(date.getDate() + 2);
    }

    return date.toISOString().split("T")[0];
  };

  const isNameValid = form.name.trim().length >= 3;
  const isDescriptionValid = form.description.trim().length >= 3;
  const isAdvanceValid =
    form.advanceAmount !== "" && Number(form.advanceAmount) >= 0;

  const isFormComplete =
    isDescriptionValid &&
    isNameValid &&
    isProfileReady &&
    form.mode &&
    form.frequency &&
    form.name.trim() &&
    form.start_date &&
    form.description.trim() &&
    isInstallmentAmountValid &&
    isMobileValid &&
    isInstallmentValid &&
    isAdvanceValid;

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleModeChange = (e) => {
    const newMode = e.target.value;

    setForm((prev) => ({
      ...prev,
      mode: newMode,
      start_date: "",
    }));
  };

  const isValidDate = () => {
    const selected = new Date(form.start_date);
    const minDate = new Date(getMinStartDate());

    selected.setHours(0, 0, 0, 0);
    minDate.setHours(0, 0, 0, 0);

    return selected >= minDate;
  };

  const validate = () => {
    if (!isFormComplete) return "All fields are mandatory";

    if (!isValidDate()) {
      return form.mode === "NACH"
        ? "For NACH, start date must be at least 2 days from today"
        : "For UPI Auto Pay, start date must be at least 1 day from today";
    }
    return "";
  };

  const submit = async () => {
    setForm(initialForm);
    const validationError = validate();

    if (validationError) {
      setError(validationError);
      return;
    }

    if (!profile) {
      setError("Profile not loaded. Please wait.");
      return;
    }

    setGlobalLoading(true);
    setError("");
    setIsLoading(true);

    const finalPayload = {
      api_id: profile.user_id,
      api_key: profile.api_key,
      token_id: profile.token_id,

      type: "REGULAR",
      mode: form.mode,

      customer: {
        mobile_number: form.mobile,
        name: form.name,
      },

      client_meta: {
        description: form.description,
      },

      schedule: {
        frequency: form.frequency,
        time_zone: "Asia/Kolkata",
        advance_amount: Number(form.advanceAmount || 0),
        amount: Number(form.amount * form.installment_count),
        installment_count: Number(form.installment_count),
        start_date: form.start_date,
      },
    };

    try {
      setViewLoading(true);

      const response = await createMandate(finalPayload);
      const createdMandate = response.data;

      if (response?.status?.code !== 200) {
        showErrorToast(response?.data?.status?.message || "Failed to Create");
        return;
      }
      setSelectedMandate(createdMandate);
      setPayload(createdMandate);
      setMandates((prev) => [...prev, createdMandate]);
      onSuccess?.();
      showSuccessToast("Mandate created successfully 🎉");
      setViewOpen(true);
    } catch (err) {
      const message = err.response?.data?.message || "Failed to create mandate";
      setError(err.response?.data?.message || "Failed to create mandate");
      showErrorToast(message);
    } finally {
      setIsLoading(false);
      setGlobalLoading(false);
      setViewLoading(false);
    }
  };

  return (
    <>
      <div className="bg-gray-100 flex justify-center mt-2">
        <div className="bg-white w-full max-w-7xl rounded-2xl shadow-xl p-2 pl-4 pr-4">
          <h1 className="text-2xl font-semibold text-center mb-2">
            Create Mandate
          </h1>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {/* Mode */}
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium">
                Mandate Mode<span className="text-red-500">*</span>
              </label>
              <select
                name="mode"
                value={form.mode}
                onChange={handleModeChange}
                className="border rounded-lg px-4 py-1"
                required
              >
                {modeLabels.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Frequency */}
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium">
                Debit Frequency<span className="text-red-500">*</span>
              </label>
              <select
                name="frequency"
                value={form.frequency}
                onChange={handleChange}
                className="border rounded-lg px-4 py-1"
                required
              >
                {FREQUENCIES.map((f) => (
                  <option key={f}>{f}</option>
                ))}
              </select>
            </div>

            {/* Name */}
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium">
                Customer Full Name<span className="text-red-500">*</span>
              </label>

              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                className={`border rounded-lg px-4 py-1 ${
                  form.name && form.name.length < 3 ? "border-red-500" : ""
                }`}
                placeholder="Name"
                required
              />

              {form.name && form.name.length < 3 && (
                <span className="text-xs text-red-500">
                  Minimum 3 characters required
                </span>
              )}
            </div>

            {/* Mobile */}
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium">
                Mobile Number<span className="text-red-500">*</span>
              </label>
              <input
                name="mobile"
                value={form.mobile}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, "");
                  if (v.length <= 10) {
                    setForm({ ...form, mobile: v });
                  }
                }}
                maxLength={10}
                inputMode="numeric"
                className="border rounded-lg px-4 py-1"
                placeholder="Mobile no."
                required
              />

              {!isMobileValid && form.mobile.length > 0 && (
                <span className="text-red-500 text-sm">
                  Mobile number must be exactly 10 digits
                </span>
              )}
            </div>

            {/* Start Date */}
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium">
                Installment Start Date<span className="text-red-500">*</span>
              </label>
              <input
                name="start_date"
                type="date"
                value={form.start_date}
                min={getMinStartDate()}
                onChange={handleChange}
                className="border rounded-lg px-4 py-1"
                required
              />
            </div>

            {/* Installments */}
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium">
                Number of Installments (≥ 1)
                <span className="text-red-500">*</span>
              </label>

              <input
                name="installment_count"
                type="number"
                min={1}
                step={1}
                value={form.installment_count}
                onChange={(e) => {
                  const value = e.target.value;

                  if (value === "") {
                    setForm({ ...form, installment_count: "" });
                    return;
                  }

                  const numberValue = Number(value);

                  if (numberValue > 0) {
                    setForm({
                      ...form,
                      installment_count: numberValue,
                    });
                  }
                }}
                className="border rounded-lg px-4 py-1"
                placeholder="Installments ≥ 1"
                required
              />

              {form.installment_count !== "" && form.installment_count <= 0 && (
                <span className="text-red-500 text-sm">
                  Installments must be greater than 0
                </span>
              )}
            </div>

            {/* Installments */}
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium">
                Installment Amount ( &gt; ₹ 15)
                <span className="text-red-500">*</span>
              </label>

              <input
                name="amount"
                type="number"
                min={16}
                step="any"
                value={form.amount}
                onChange={(e) => {
                  const value = e.target.value;

                  if (value === "") {
                    setForm({ ...form, amount: "" });
                    return;
                  }

                  const numberValue = Number(value);

                  if (numberValue >= 0) {
                    setForm({
                      ...form,
                      amount: numberValue,
                    });
                  }
                }}
                className="border rounded-lg px-4 py-1"
                placeholder="Installment > ₹ 15"
                required
              />

              {form.amount !== "" && form.amount <= 15 && (
                <span className="text-red-500 text-sm">
                  Per installment amount must be greater than 15
                </span>
              )}
            </div>

            {/* Total Amount */}
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium">Total Debit Amount</label>

              <input
                type="number"
                value={totalAmount}
                readOnly
                className="border rounded-lg px-4 py-1 bg-gray-50 cursor-not-allowed"
                placeholder="Auto-calculated — read only"
              />
              {form.installment_count > 0 && (
                <p className="text-xs text-gray-500">
                  Total = Installment Amount × Installments
                </p>
              )}
            </div>

            {/* Advance Amount */}
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium">
                Advance Amount<span className="text-red-500">*</span>
              </label>

              <input
                name="advanceAmount"
                type="number"
                min={0}
                step="any"
                value={form.advanceAmount}
                onChange={(e) => {
                  const value = e.target.value;

                  if (value === "") {
                    setForm({ ...form, advanceAmount: "" });
                    return;
                  }

                  const numberValue = Number(value);

                  if (numberValue >= 0) {
                    setForm({
                      ...form,
                      advanceAmount: numberValue,
                    });
                  }
                }}
                className="border rounded-lg px-4 py-1"
                placeholder="Advance Amount"
              />
              {form.advanceAmount === "" && !isFormComplete && (
                <span className="text-red-500 text-sm">
                  Advance amount is required
                </span>
              )}
            </div>

            {/* Description */}
            <div className="flex flex-col gap-1 md:col-span-3">
              <label className="text-sm font-medium">
                Remarks<span className="text-red-500">*</span>
              </label>
              <textarea
                name="description"
                rows={2}
                value={form.description}
                onChange={handleChange}
                className={`border rounded-lg px-4 py-1 ${
                  form.description && form.description.length < 3
                    ? "border-red-500"
                    : ""
                }`}
                placeholder="Reason for debit"
                required
              />

              {form.description && form.description.length < 3 && (
                <span className="text-xs text-red-500">
                  Minimum 3 characters required
                </span>
              )}
            </div>
          </div>

          <div className="mt-2 p-4 flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg text-sm text-gray-700">
            <div>
              <p className="font-semibold mb-2">Mandate Mode Instructions</p>
              <ul className="list-disc list-inside space-y-1">
                <li>
                  <strong>UPI Auto Pay</strong>: The mandate must be created at
                  least <strong>one day before</strong> the debit installment
                  date.
                </li>
                <li>
                  <strong>NACH</strong>: The mandate must be created at least{" "}
                  <strong>two days before</strong> the debit installment date.
                </li>
                <li>
                  After creation, the mandate must be{" "}
                  <strong>approved by the customer</strong>.
                </li>
                <li>
                  Only mandates that are{" "}
                  <strong>not approved by the customer</strong> can be deleted.
                </li>
              </ul>
            </div>
            <div>
              <img src="/media/idspayautodebit api-.png" alt="" className="w-[200px] h-[100px] pointer-events-none"/>
            </div>
          </div>

          {/* instructions */}

          <div className="flex w-full items-center rounded-xl my-4 gap-2 justify between border-2 border-dashed border-blue-500 py-4 px-12 ">
            <div className="w-full">
              <div className="text-xl font-bold text-blue-500">
                Required kyc app installation
              </div>
              <p className="text-sm text-gray-500">
                To enable the mandate creation feature, you must complete your
                KYC. This KYC is a one-time requirement for your API account.
              </p>
            </div>

            <div className="flex flex-col justify-center items-center text-blue gap-4">
              <a
                download
                href="https://idspay.in/public/images/IDSPay%20KYC%20App.apk"
                className="bg-blue-500 active:bg-slate-700 hover:bg-gray-700 text-white flex font-semibold items-center gap-2 text-nowrap  rounded-xl px-4 py-2"
              >
                <Download />
                Download kyc app
              </a>
              <a
                href="/assets/IDSPay KYC APP Intro_compressed.pdf"
                target="_blank"
                className="bg-blue-500 active:bg-slate-700 hover:bg-gray-700 font-semibold flex items-center gap-2 text-white p-2rounded rounded-xl py-2 px-4"
              >
                <Info />
                KYC Instruction
              </a>
            </div>

            <div></div>
          </div>

          {error && <p className="text-red-600 mt-4">{error}</p>}
          <button
            onClick={submit}
            disabled={!isFormComplete || isLoading}
            className={`mt-4 w-full py-3 rounded-lg font-medium text-white
            ${
              isFormComplete && !isLoading
                ? "bg-blue-600 hover:bg-blue-700"
                : "bg-gray-400 cursor-not-allowed"
            }
            `}
          >
            {isLoading ? "Creating..." : "Create Mandate"}
          </button>
        </div>
        <ViewMandateModal
          open={viewOpen}
          loading={viewLoading}
          data={selectedMandate}
          onSuccess={onSuccess}
          setGlobalLoading={setGlobalLoading}
          onClose={() => {
            setViewOpen(false);
            setSelectedMandate(null);
          }}
        />
      </div>
    </>
  );
}
